import {
  Box,
  Button,
  Icon,
  IconButton,
  Skeleton,
  Stack,
  SxProps,
  TextField,
  TextFieldProps,
  Theme,
  Typography,
} from '@mui/material';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { CloseIcon, EditIcon, TypographyWithOverflowHint } from '@schooly/style';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { formatISO } from 'date-fns';
import {
  FC,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import {
  DefaultStreamChatGenerics,
  StreamMessage,
  useMessageInputContext,
} from 'stream-chat-react';

import { useThreads } from '../ThreadContext';

export type ChannelInputProps = {
  onSubmit?: (event: React.KeyboardEvent<HTMLInputElement>, text: string) => void;
  sx?: SxProps<Theme>;
};

export type ChannelInputRef = {
  onMessageEdit: (message: StreamMessage<DefaultStreamChatGenerics>) => void;
};

const defaultInputSx: SxProps<Theme> = {
  gap: 1.25,
  px: 2.5,
};

export const ChannelInput = forwardRef<ChannelInputRef, ChannelInputProps>(
  ({ onSubmit, sx }, ref) => {
    const { client } = useThreads();
    const { text, handleChange, handleSubmit, setText } = useMessageInputContext();
    const { formatMessage } = useIntl();
    const [messageToEdit, setMessageToEdit] =
      useState<StreamMessage<DefaultStreamChatGenerics> | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const previousText = useRef<string>('');
    const { getConfirmation } = useConfirmationDialog();

    const isTextEdited = messageToEdit ? messageToEdit.text?.trim() !== text.trim() : false;

    // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
    useImperativeHandle(
      ref,
      () => ({
        onMessageEdit: (message: StreamMessage<DefaultStreamChatGenerics>) => {
          if (!message.text) return;
          if (!messageToEdit && !!text) {
            previousText.current = text;
          }

          setMessageToEdit(message);
          setText(message.text);

          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        },
      }),
      [messageToEdit, text],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
    const clearMessageToEdit = useCallback(() => {
      setMessageToEdit(null);
      setText(previousText.current ? previousText.current : '');
      previousText.current = '';
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
    const handleMessageUpdate = useCallback(
      async (message: StreamMessage<DefaultStreamChatGenerics>) => {
        if (text && isTextEdited) {
          await client?.partialUpdateMessage(message.id, {
            set: {
              text,
              edited_at: formatISO(newDateTimezoneOffset()),
            },
          });
        }

        clearMessageToEdit();
      },
      [client, clearMessageToEdit, text],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && !event.shiftKey && !!text) {
          event.preventDefault();
          if (messageToEdit) {
            handleMessageUpdate(messageToEdit);
          } else {
            handleSubmit(event);
            onSubmit?.(event, text);
          }
        }
      },
      [handleSubmit, text],
    );

    // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
    const handleEditCancel = useCallback(async () => {
      if (isTextEdited) {
        const isConfirmed = await getConfirmation({
          confirmTextId: 'messages-CancelEditDiscard',
          cancelTextId: 'no',
          content: (
            <Typography variant="h1">
              {formatMessage({ id: 'messages-CancelEditConfirmation' })}
            </Typography>
          ),
        });

        if (!isConfirmed) return;
      }

      clearMessageToEdit();
    }, [clearMessageToEdit, isTextEdited]);

    return (
      <Stack
        direction="row"
        className="thread-channel-input"
        sx={sx ? [defaultInputSx, ...(Array.isArray(sx) ? sx : [sx])] : defaultInputSx}
      >
        <ChannelTextField
          value={text}
          inputRef={inputRef}
          onChange={(event) => {
            handleChange(event as any);
          }}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage({ id: 'messages-MessageTitle' })}
        >
          {messageToEdit && (
            <Box
              width="100%"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                px={1.25}
                pt={1}
                gap={0.75}
              >
                <Icon inverse sx={{ color: 'common.grey' }}>
                  <EditIcon />
                </Icon>
                <TypographyWithOverflowHint
                  sx={{ flex: 1, width: '100%' }}
                  variant="h3"
                  color="common.grey"
                >
                  {messageToEdit.text}
                </TypographyWithOverflowHint>

                <IconButton inverse onClick={handleEditCancel}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>
          )}
        </ChannelTextField>
        <Stack alignSelf="flex-end">
          <Button
            disabled={!text.trim()}
            onClick={() => {
              if (messageToEdit) handleMessageUpdate(messageToEdit);
              else handleSubmit();
            }}
          >
            {formatMessage({ id: messageToEdit ? 'action-Update' : 'messages-ThreadChannelSend' })}
          </Button>
        </Stack>
      </Stack>
    );
  },
);

export const ChannelTextField: FC<
  PropsWithChildren<Omit<TextFieldProps, 'variant'> & { isLoading?: boolean }>
> = ({ isLoading, children, ...props }) => {
  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
    >
      {isLoading && (
        <Box sx={{ zIndex: 1, position: 'absolute', top: 14, left: 10, width: '35%' }}>
          <Skeleton variant="rectangular" height={16} />
        </Box>
      )}
      <TextField
        {...props}
        fullWidth
        maxRows={5}
        minRows={1}
        multiline
        sx={{
          '& .MuiOutlinedInput-input': {
            backgroundColor: 'white',
            borderRadius: '8px',
          },

          '& .MuiInputBase-root': {
            border: 'none !important',
            backgroundColor: 'white',
            paddingTop: children ? 4 : 0,
          },

          '.MuiInputBase-input': {
            img: {
              margin: '0px !important',
              display: 'inline !important',
              height: 'auto',
              width: '50px',
            },
          },
          width: '100%',
          '&& .MuiFilledInput-root': {
            backgroundColor: 'white !important',
          },
        }}
      />
      {children}
    </Box>
  );
};
